import { useEffect, useMemo, useState } from "react";
import { Eye, Search, RefreshCw, ArrowLeft, Layers } from "lucide-react";
import { Input } from "./ui/input";
import { fetchOeilOverview } from "../lib/api";
import { eur } from "../lib/prices";
import { CardImage } from "./CardImage";
import { Sparkline } from "./Sparkline";
import { CM_CONDITION_ORDER, CM_CONDITION_LABEL, OS_COLOR, DeltaBadge } from "./RealMarketBlock";
import { useSetAsset } from "../hooks/useSetAsset";
import { handleSetAssetError } from "../lib/images";
import { buildSidebarBlocs } from "../lib/sidebar";

// Tri « naturel » d'un numéro de carte : chiffres purs d'abord (1, 2, 10),
// puis préfixe + chiffres (SV001, TG12), puis lettres seules — même règle que
// localid_sort_key() côté serveur.
const localIdKey = (v) => {
  const s = String(v ?? "");
  const m = /^([A-Za-z]*)(\d+)(.*)$/.exec(s);
  if (!m) return [2, s, 0, ""];
  return [m[1] ? 1 : 0, m[1].toUpperCase(), parseInt(m[2], 10), m[3]];
};
const cmpLocalId = (a, b) => {
  const ka = localIdKey(a), kb = localIdKey(b);
  for (let i = 0; i < 4; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

// Onglet « Œil de Sauron » : toutes les cartes ayant au moins une capture de
// prix réel sur Cardmarket. Deux niveaux (retour utilisateur du 2026-10-03,
// « les collections apparaissent en tuiles, on clique dessus et là il y a
// les cartes ») : (1) les collections en tuiles (logo, symbole, nombre de
// cartes, sous-total réel / cote), (2) les cartes d'une collection, avec
// retour. Filtre d'état avec « Tous les états » par défaut : chaque carte
// sous son état capturé le plus récemment (sinon une carte observée
// seulement en Excellent restait invisible tant qu'on ne cliquait pas
// précisément sur Excellent).
const SORTS = [
  ["number", "Numéro"],
  ["last", "Dernière capture"],
  ["delta", "Écart réel / cote"],
  ["real", "Prix réel"],
  ["name", "Nom"],
];

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffH = (Date.now() - d.getTime()) / 3600000;
  if (diffH < 1) return "il y a moins d'une heure";
  if (diffH < 24) return `il y a ${Math.round(diffH)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};
const fmtDay = (day) => { if (!day) return "—"; const [y, m, d] = day.split("-"); const today = new Date().toISOString().slice(0, 10); return day === today ? "aujourd'hui" : `${d}/${m}${y !== today.slice(0, 4) ? `/${y.slice(2)}` : ""}`; };

const Kpi = ({ label, value, sub, tone }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-3.5 py-3 min-w-0">
    <div className="text-[11px] uppercase tracking-wider text-zinc-500 truncate">{label}</div>
    <div className={`stat-value text-2xl mt-0.5 ${tone || "text-white"}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 truncate">{sub}</div>}
  </div>
);

// Tuile d'une collection (niveau 1).
const SetTile = ({ group, onOpen }) => {
  const logoSrc = useSetAsset(group.set_id, "logo", group.set_logo);
  const symbolSrc = useSetAsset(group.set_id, "symbol", group.set_symbol);
  const delta = group.cote > 0 ? ((group.real - group.cote) / group.cote) * 100 : null;
  return (
    <button className="oeil-tile" onClick={() => onOpen(group.set_id)}>
      <div className="oeil-tile-top">
        {logoSrc ? <img src={logoSrc} alt="" className="oeil-tile-logo" onError={handleSetAssetError} />
                 : <Layers className="w-6 h-6 text-zinc-600" />}
        {symbolSrc && <img src={symbolSrc} alt="" className="oeil-tile-symbol" onError={handleSetAssetError} />}
      </div>
      <div className="oeil-tile-name">{group.set_name || group.set_id}</div>
      <div className="oeil-tile-meta">
        <span><b>{group.items.length}</b> carte{group.items.length > 1 ? "s" : ""}{group.owned > 0 && <> · {group.owned} possédée{group.owned > 1 ? "s" : ""}</>}</span>
        <span>réel <b style={{ color: OS_COLOR }}>{eur(group.real)}</b>{group.cote > 0 && <> / cote {eur(group.cote)}</>}</span>
      </div>
      {delta != null && <div><DeltaBadge pct={delta} /></div>}
    </button>
  );
};

// En-tête de la collection ouverte (niveau 2).
const SetHeader = ({ group, onBack }) => {
  const logoSrc = useSetAsset(group.set_id, "logo", group.set_logo);
  const symbolSrc = useSetAsset(group.set_id, "symbol", group.set_symbol);
  return (
    <div className="mb-3">
      <button className="oeil-back" onClick={onBack}><ArrowLeft className="w-4 h-4" />Toutes les collections</button>
      <div className="oeil-group" style={{ marginTop: 4 }}>
        {logoSrc && <img src={logoSrc} alt="" className="oeil-group-logo" onError={handleSetAssetError} />}
        <span className="oeil-group-name">
          {symbolSrc && <img src={symbolSrc} alt="" className="oeil-group-symbol" onError={handleSetAssetError} />}
          <span className="truncate">{group.set_name || group.set_id}</span>
        </span>
        <span className="oeil-group-meta">
          {group.items.length} carte{group.items.length > 1 ? "s" : ""}
          {group.real > 0 && <> · réel <b className="text-zinc-300">{eur(group.real)}</b>{group.cote > 0 && <> / cote {eur(group.cote)}</>}</>}
        </span>
      </div>
    </div>
  );
};

export const OeilDeSauronView = ({ onCardClick, seriesData = [], sidebarPrefs }) => {
  const [condition, setCondition] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [openSet, setOpenSet] = useState(null);
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [sort, setSort] = useState("number");

  // Ordre des collections = celui de l'appli (blocs du plus récent au plus
  // ancien, sets dans l'ordre de blocs.js, masquages/ordre des Réglages
  // appliqués) — « toujours pareil », demande utilisateur du 2026-10-03.
  const setRank = useMemo(() => {
    const rank = {};
    let i = 0;
    buildSidebarBlocs(seriesData || [], sidebarPrefs || {}).forEach(bloc => {
      const parts = bloc.subBlocs?.length ? bloc.subBlocs : [bloc];
      parts.forEach(part => (part.sets || []).forEach(s => { if (!(s.id in rank)) rank[s.id] = i++; }));
    });
    return rank;
  }, [seriesData, sidebarPrefs]);

  const load = () => {
    setLoading(true); setError(null);
    fetchOeilOverview({ condition })
      .then(setData)
      .catch(() => setError("Impossible de charger les observations."))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [condition]);

  const items = useMemo(() => {
    let list = data?.items || [];
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter(i => (i.name || "").toLowerCase().includes(needle) || (i.set_name || "").toLowerCase().includes(needle));
    if (ownedOnly) list = list.filter(i => i.status === "owned");
    const by = {
      number: (a, b) => cmpLocalId(a.local_id, b.local_id),
      last: (a, b) => (b.last_day || "").localeCompare(a.last_day || ""),
      delta: (a, b) => (a.delta_pct ?? 999) - (b.delta_pct ?? 999),
      real: (a, b) => b.real - a.real,
      name: (a, b) => (a.name || "").localeCompare(b.name || "", "fr"),
    }[sort];
    return [...list].sort(by);
  }, [data, q, ownedOnly, sort]);

  // Regroupement par collection, les plus récentes en premier.
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach(i => {
      const key = i.set_id || "?";
      if (!map.has(key)) map.set(key, { set_id: key, set_name: i.set_name, set_logo: i.set_logo, set_symbol: i.set_symbol,
        set_release: i.set_release || "", items: [], real: 0, cote: 0, owned: 0 });
      const g = map.get(key);
      g.items.push(i);
      g.real += i.real || 0;
      g.cote += i.cote || 0;
      if (i.status === "owned") g.owned += 1;
    });
    // Ordre de l'appli d'abord ; un set inconnu des blocs (jamais) retombe
    // sur la date de sortie.
    return [...map.values()].sort((a, b) => {
      const ra = setRank[a.set_id], rb = setRank[b.set_id];
      if (ra != null && rb != null) return ra - rb;
      if (ra != null) return -1;
      if (rb != null) return 1;
      return (b.set_release || "").localeCompare(a.set_release || "");
    });
  }, [items, setRank]);

  const current = openSet ? groups.find(g => g.set_id === openSet) : null;
  const k = data?.kpis;
  const siblingIds = current ? current.items.map(i => i.card_id) : [];

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <span className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "rgba(245,158,11,.14)", color: OS_COLOR }}>
          <Eye className="w-5 h-5" />
        </span>
        <h2 className="text-2xl font-bold text-white">Œil de Sauron</h2>
        <span className="text-sm text-zinc-500">prix réels relevés sur Cardmarket</span>
        <button onClick={load} title="Actualiser" className="ml-auto p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">
        Chaque visite d'une fiche Cardmarket avec l'extension relève les annonces en français du jour. Le prix réel
        est la moins chère d'entre elles, à comparer à la cote théorique.
      </p>

      {k && !openSet && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Kpi label="Cartes observées" value={k.cards} sub={`dont ${k.owned_observed} possédée${k.owned_observed > 1 ? "s" : ""}`} />
          <Kpi label="Captures" value={k.captures} sub={`dernière ${fmtWhen(k.last_capture)}`} />
          <Kpi label="Réel vs cote" value={k.mean_delta_pct == null ? "—" : `${k.mean_delta_pct >= 0 ? "+" : "−"}${Math.abs(k.mean_delta_pct).toFixed(1)} %`}
            sub={`écart moyen, ${condition === "all" ? "tous états" : CM_CONDITION_LABEL[condition]} FR`}
            tone={k.mean_delta_pct == null ? "text-zinc-500" : k.mean_delta_pct >= 0 ? "text-emerald-400" : "text-red-400"} />
          <Kpi label="Valeur réelle possédée" value={k.owned_observed ? eur(k.real_value_owned) : "—"}
            sub={k.owned_observed ? `contre ${eur(k.cote_value_owned)} en cote` : "aucune carte possédée observée"}
            tone={k.owned_observed ? "" : "text-zinc-500"} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button onClick={() => setCondition("all")}
          className={`text-xs px-2.5 py-1 rounded-full border ${condition === "all" ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
          Tous les états
        </button>
        {CM_CONDITION_ORDER.map(c => (
          <button key={c} onClick={() => setCondition(c)}
            className={`text-xs px-2.5 py-1 rounded-full border ${condition === c ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
            {CM_CONDITION_LABEL[c]}
          </button>
        ))}
        <select value={sort} onChange={(e) => setSort(e.target.value)} title="Ordre des cartes dans une collection"
          className="bg-zinc-900 border border-zinc-700 rounded-lg px-2.5 py-1 text-xs text-zinc-300">
          {SORTS.map(([k2, label]) => <option key={k2} value={k2}>Tri : {label}</option>)}
        </select>
        <button onClick={() => setOwnedOnly(v => !v)}
          className={`text-xs px-2.5 py-1 rounded-full border ${ownedOnly ? "bg-zinc-700 border-zinc-600 text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
          Possédées seulement
        </button>
        <div className="relative ml-auto w-full sm:w-56">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer par nom…" className="search-input pl-8 h-8 text-xs" />
        </div>
      </div>

      {error && <div className="text-sm text-red-400 mb-3">{error}</div>}
      {loading && !data ? (
        <div className="text-sm text-zinc-500">Chargement…</div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-zinc-500 text-center py-12 bg-zinc-900/40 border border-zinc-800 rounded-xl">
          {data?.items?.length
            ? "Aucune carte ne correspond à ces filtres."
            : `Aucune capture${condition === "all" ? "" : ` en ${CM_CONDITION_LABEL[condition]}`} pour l'instant. Visite une fiche Cardmarket avec l'extension : elle apparaîtra ici.`}
        </div>
      ) : !current ? (
        <div className="oeil-tiles">
          {groups.map(g => <SetTile key={g.set_id} group={g} onOpen={setOpenSet} />)}
        </div>
      ) : (
        <div>
          <SetHeader group={current} onBack={() => setOpenSet(null)} />
          <div className="flex flex-col gap-1.5">
            <div className="oeil-row oeil-head">
              <span /><span>Carte</span><span>Réel</span><span className="oeil-hide">Cote</span><span className="oeil-hide">Écart</span><span className="oeil-hide">Tendance</span><span>Capture</span>
            </div>
            {current.items.map(i => (
              <button key={i.card_id} className="oeil-row text-left" onClick={() => onCardClick({ id: i.card_id }, siblingIds)}>
                <span className="oeil-thumb">
                  {i.image ? <CardImage card={{ id: i.card_id, image: i.image, image_full: i.image_full, name: i.name, set: { id: i.set_id } }} quality="low" className="w-full h-full object-cover rounded" /> : null}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-white truncate">
                    {i.status === "owned" && <span className="oeil-dot oeil-dot-owned" title="possédée" />}
                    {i.status === "wanted" && <span className="oeil-dot oeil-dot-wanted" title="recherchée" />}
                    {i.name}{i.local_id ? <span className="text-zinc-500 font-normal"> · {i.local_id}</span> : null}
                  </span>
                  <span className="block text-xs text-zinc-500 truncate">{i.set_name || i.set_id}</span>
                </span>
                <span>
                  <span className="stat-value text-base tabular-nums whitespace-nowrap" style={{ color: OS_COLOR }}>{eur(i.real)}</span>
                  {condition === "all" && i.condition && <span className="block"><span className="oeil-cond">{CM_CONDITION_LABEL[i.condition] || i.condition}</span></span>}
                  {i.offers > 1 && <span className="block text-[10px] text-zinc-600">{i.offers} annonces, jusqu'à {eur(i.real_max)}</span>}
                </span>
                <span className="oeil-hide text-xs text-zinc-400 tabular-nums">{i.cote != null ? eur(i.cote) : "—"}</span>
                <span className="oeil-hide"><DeltaBadge pct={i.delta_pct} /></span>
                <span className="oeil-hide"><Sparkline values={i.spark} color={OS_COLOR} /></span>
                <span className="text-xs text-zinc-500">{fmtDay(i.last_day)}<span className="block text-[10px] text-zinc-600">{i.captures} visite{i.captures > 1 ? "s" : ""}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
